import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    TextField,
    Button,
    Snackbar,
    Alert,
    Divider,
    Chip,
    Stack,
    CircularProgress,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { CardRoot, CardHeader, CardBody, ActionsRow, FieldLabel } from './PreloadedDataConfigStyle';
import {
    DEFAULT_PRELOADED,
    fetchPreloadedProperties,
    savePreloadedProperties,
    toUniqueTrimmedList
} from '../../utils/preloadedData';

const FIELD_CONFIG = [
    { key: 'responsible', label: 'Responsable' },
    { key: 'operator', label: 'Operador' },
    { key: 'machineCode', label: 'Código de máquina' },
    { key: 'product', label: 'Producto' },
];

const LS_PRELOADED_CACHE_KEY = (userId) => `aps_preloaded_cache_v2_${userId}`;

function syncApsPreloadedCache(userId, properties) {
    if (!userId) return;
    try {
        localStorage.setItem(
            LS_PRELOADED_CACHE_KEY(userId),
            JSON.stringify({
                ts: Date.now(),
                responsableOptions: toUniqueTrimmedList(properties?.responsible || []),
                operadorOptions: toUniqueTrimmedList(properties?.operator || []),
                codigoMaquinaOptions: toUniqueTrimmedList(properties?.machineCode || []),
                productoOptions: toUniqueTrimmedList(properties?.product || [])
            })
        );
    } catch {
        // ignore
    }
}

const PreloadedDataConfig = ({ userId }) => {
    const [loading, setLoading] = useState(true);
    const [properties, setProperties] = useState(DEFAULT_PRELOADED);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [dirty, setDirty] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const updatedAtLabel = useMemo(() => {
        if (!updatedAt) return '—';
        try {
            return new Date(updatedAt).toLocaleString();
        } catch {
            return '—';
        }
    }, [updatedAt]);

    const loadFromApi = async () => {
        setLoading(true);
        try {
            const data = await fetchPreloadedProperties(userId);
            const nextProperties = data.properties || DEFAULT_PRELOADED;
            setProperties(nextProperties);
            setUpdatedAt(data.updatedAt || null);
            setDirty(false);
            syncApsPreloadedCache(userId, nextProperties);
        } catch (e) {
            console.error(e);
            setSnackbar({
                open: true,
                message: 'No se pudieron cargar los datos precargados.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) loadFromApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handleChangeField = (fieldKey) => (_, newValues) => {
        const cleaned = toUniqueTrimmedList(newValues);
        setProperties((prev) => ({
            ...prev,
            [fieldKey]: cleaned,
        }));
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            const res = await savePreloadedProperties(userId, properties);
            setProperties(res.properties);
            setUpdatedAt(res.updatedAt || null);
            setDirty(false);
            syncApsPreloadedCache(userId, res.properties);

            setSnackbar({
                open: true,
                message: 'Datos precargados guardados correctamente.',
                severity: 'success',
            });
        } catch (e) {
            console.error(e);
            setSnackbar({
                open: true,
                message: 'No se pudieron guardar los datos precargados.',
                severity: 'error',
            });
        }
    };

    const handleReload = async () => {
        await loadFromApi();
        setSnackbar({
            open: true,
            message: 'Se recargó la configuración guardada.',
            severity: 'info',
        });
    };

    const handleClearAll = () => {
        setProperties(DEFAULT_PRELOADED);
        setDirty(true);
        setSnackbar({
            open: true,
            message: 'Se limpiaron los valores (aún no se guarda).',
            severity: 'warning',
        });
    };

    return (
        <CardRoot>
            <CardHeader>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Datos precargados
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Guarda valores por defecto para reutilizarlos en otras pantallas (Responsable, Operador, Código de máquina y Producto).
                    </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">
                        Última actualización
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {updatedAtLabel}
                    </Typography>
                </Box>
            </CardHeader>

            <Divider />

            <CardBody>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
                        <CircularProgress size={22} />
                        <Typography variant="body2" color="text.secondary">
                            Cargando datos precargados...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            {FIELD_CONFIG.map((field) => (
                                <Grid item xs={12} md={6} key={field.key}>
                                    <FieldLabel>{field.label}</FieldLabel>

                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        value={properties[field.key] || []}
                                        options={properties[field.key] || []}
                                        onChange={handleChangeField(field.key)}
                                        renderTags={(value, getTagProps) => (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                flexWrap="wrap"
                                                sx={{
                                                    maxHeight: 84,
                                                    overflowY: 'auto',
                                                    pr: 0.5,
                                                    pb: 0.5,
                                                }}
                                            >
                                                {value.map((option, index) => (
                                                    <Chip
                                                        size="small"
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        key={`${option}-${index}`}
                                                        sx={{
                                                            borderRadius: 999,
                                                            backgroundColor: 'rgba(255,153,0,0.10)',
                                                            border: '1px solid rgba(255,153,0,0.30)',
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Escribe y presiona Enter…"
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: '#fff',
                                                    },
                                                }}
                                            />
                                        )}
                                    />

                                    <Typography variant="caption" color="text.secondary">
                                        Tip: Puedes agregar varios valores (Enter) y se guardan sin duplicados.
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>

                        <ActionsRow>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    disabled={!dirty}
                                    sx={{
                                        backgroundColor: 'var(--primary-color)',
                                        '&:hover': { backgroundColor: 'var(--primary-color-dark)' },
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                    }}
                                >
                                    Guardar
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<RestartAltIcon />}
                                    onClick={handleReload}
                                    sx={{
                                        borderColor: 'rgba(0,0,0,0.15)',
                                        color: 'rgba(0,0,0,0.75)',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        '&:hover': { borderColor: 'rgba(0,0,0,0.25)' },
                                    }}
                                >
                                    Recargar guardado
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={handleClearAll}
                                    sx={{
                                        borderColor: 'rgba(244,67,54,0.35)',
                                        color: 'rgba(244,67,54,0.95)',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        '&:hover': { borderColor: 'rgba(244,67,54,0.60)' },
                                    }}
                                >
                                    Limpiar todo
                                </Button>
                            </Box>

                            {dirty && (
                                <Typography variant="caption" color="text.secondary">
                                    * Tienes cambios sin guardar.
                                </Typography>
                            )}
                        </ActionsRow>
                    </>
                )}
            </CardBody>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </CardRoot>
    );
};

export default PreloadedDataConfig;
